import { privateApi } from '@/config/axios.config';

const API_BASE = '/condominium/billing';

export interface InvoiceItem {
  id?: string;
  description: string;
  longDescription?: string;
  quantity: number;
  unit: string;
  price: number;
  tax: string;
  amount: number;
}

export interface Invoice {
  id: string;
  tenantId: string;
  residentId?: string;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  status: 'pending' | 'paid' | 'overdue' | 'cancelled' | 'draft' | 'partially_paid';
  currency: 'DOP' | 'USD';
  totalAmount: number;
  paidAmount: number;
  taxAmount: number;
  discountAmount: number;
  description?: string;
  commercialNote?: string;
  notes?: string;
  items: InvoiceItem[];
  tags?: string[];
  allowedPaymentMethods?: string[];
  agent?: string;
  isRecurring: boolean;
  preventReminders: boolean;
  paymentDate?: string;
  paymentMethod?: string;
  paymentReference?: string;
  createdAt: string;
  updatedAt: string;
  resident?: {
    id: string;
    name: string;
    apartment: string;
    tower: string;
  };
}

export interface CreateInvoiceDTO {
  residentId: string;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  status?: 'pending' | 'paid' | 'overdue' | 'cancelled' | 'draft' | 'partially_paid';
  currency?: 'DOP' | 'USD';
  items: InvoiceItem[];
  description?: string;
  commercialNote?: string;
  notes?: string;
  tags?: string[];
  allowedPaymentMethods?: string[];
  agent?: string;
  isRecurring?: boolean;
  discountType?: 'none' | 'percentage' | 'fixed';
  discountValue?: number;
  preventReminders?: boolean;
  showQuantityAs?: string;
  billableTasks?: string;
}

export interface UpdateInvoiceDTO {
  residentId?: string;
  invoiceDate?: string;
  dueDate?: string;
  status?: 'pending' | 'paid' | 'overdue' | 'cancelled' | 'draft' | 'partially_paid';
  currency?: 'DOP' | 'USD';
  items?: InvoiceItem[];
  description?: string;
  commercialNote?: string;
  notes?: string;
  tags?: string[];
  allowedPaymentMethods?: string[];
  agent?: string;
  isRecurring?: boolean;
  discountType?: 'none' | 'percentage' | 'fixed';
  discountValue?: number;
  preventReminders?: boolean;
  paymentDate?: string;
  paymentMethod?: string;
  paymentReference?: string;
}

export interface InvoiceFilters {
  status?: string;
  residentId?: string;
  search?: string;
  month?: string;
  year?: string;
  page?: number;
  limit?: number;
}

export interface BillingStats {
  totalInvoices: number;
  paid: number;
  pending: number;
  overdue: number;
  totalAmount: number;
  collectedAmount: number;
  pendingAmount: number;
  overdueAmount: number;
}

export const billingApiService = {
  // Obtener estadísticas
  async getStats(year?: string): Promise<BillingStats> {
    try {
      const params = year ? { year } : {};
      const response = await privateApi.get(`${API_BASE}/stats`, { params });
      const result = response.data.data;
      return result;
    } catch (error: any) {
      throw error;
    }
  },

  // Obtener todas las facturas con filtros
  async getInvoices(filters: InvoiceFilters = {}): Promise<{
    invoices: Invoice[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }> {
    try {
      const response = await privateApi.get(API_BASE, { params: filters });
      const result = response.data.data;
      return result;
    } catch (error: any) {
      throw error;
    }
  },

  // Obtener una factura por ID
  async getInvoiceById(id: string): Promise<Invoice> {
    const response = await privateApi.get(`${API_BASE}/${id}`);
    return response.data.data;
  },

  // Crear una nueva factura
  async createInvoice(data: CreateInvoiceDTO): Promise<Invoice> {
    try {
      const response = await privateApi.post(API_BASE, data);
      const result = response.data.data;
      return result;
    } catch (error: any) {
      throw error;
    }
  },

  // Actualizar una factura
  async updateInvoice(id: string, data: UpdateInvoiceDTO): Promise<Invoice> {
    const response = await privateApi.patch(`${API_BASE}/${id}`, data);
    return response.data.data;
  },

  // Eliminar una factura
  async deleteInvoice(id: string): Promise<void> {
    await privateApi.delete(`${API_BASE}/${id}`);
  },

  // Marcar como pagada
  async markAsPaid(
    id: string, 
    paymentData: { 
      paymentDate: string; // YYYY-MM-DDTHH:mm (fecha + hora combinadas)
      paymentMethod: string;
      paymentReference: string;
      receipt?: File;
      notes?: string;
    }
  ): Promise<Invoice> {
    const formData = new FormData();
    
    // Agregar campos del formulario
    formData.append('status', 'paid');
    formData.append('paymentDate', paymentData.paymentDate);
    formData.append('paymentMethod', paymentData.paymentMethod);
    formData.append('paymentReference', paymentData.paymentReference);
    
    if (paymentData.notes) {
      formData.append('notes', paymentData.notes);
    }
    
    // Agregar archivo de comprobante si existe y es válido
    if (paymentData.receipt && paymentData.receipt.size > 0) {
      formData.append('receipt', paymentData.receipt);
    }

    const response = await privateApi.patch(`${API_BASE}/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data.data;
  },

  // Descargar factura PDF
  async downloadInvoice(id: string): Promise<Blob> {
    const response = await privateApi.get(`${API_BASE}/${id}/download`, {
      responseType: 'blob',
    });
    return response.data;
  },
};

